import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function usePermissions() {
  const authStore = useAuthStore()

  const getJWTPermissions = () => {
    try {
      const token = authStore.token
      if (!token) return []

      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.permissions || []
    } catch (error) {
      return []
    }
  }

  const getJWTRole = () => {
    try {
      const token = authStore.token
      if (!token) return ''

      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.role || ''
    } catch (error) {
      return ''
    }
  }

  const userPermissions = computed(() => {
    let rolePermissions = authStore.currentUser?.role_details?.permissions || []

    if (!Array.isArray(rolePermissions) || rolePermissions.length === 0) {
      const jwtPermissions = getJWTPermissions()
      rolePermissions = jwtPermissions
    }

    const processedPermissions = Array.isArray(rolePermissions)
      ? rolePermissions.map((permission) => permission.name || permission)
      : []

    return processedPermissions
  })

  const hasPermission = (permission) => {
    if (!permission) return true

    const userRoleName =
      authStore.currentUser?.role_details?.name || authStore.currentUser?.role || getJWTRole()

    // Vérification directe du rôle superadmin/admin AVANT de vérifier les permissions
    if (
      userRoleName === 'superadmin' ||
      userRoleName === 'super_admin' ||
      userRoleName === 'admin' ||
      userRoleName === 'administrateur' ||
      userRoleName === 'SUPERADMIN' ||
      userRoleName === 'SUPER_ADMIN' ||
      userRoleName === 'ADMIN' ||
      userRoleName === 'ADMINISTRATEUR'
    ) {
      return true
    }

    const hasAccess = userPermissions.value.includes(permission)
    return hasAccess
  }

  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true

    const userRoleName =
      authStore.currentUser?.role_details?.name || authStore.currentUser?.role || getJWTRole()

    // Vérification directe du rôle superadmin/admin AVANT toute autre logique
    if (
      userRoleName === 'superadmin' ||
      userRoleName === 'super_admin' ||
      userRoleName === 'admin' ||
      userRoleName === 'administrateur' ||
      userRoleName === 'SUPERADMIN' ||
      userRoleName === 'SUPER_ADMIN' ||
      userRoleName === 'ADMIN' ||
      userRoleName === 'ADMINISTRATEUR'
    ) {
      return true
    }

    // Éviter la récursion en vérifiant directement les permissions
    const result = permissions.some((permission) => userPermissions.value.includes(permission))
    return result
  }

  const hasAllPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return true

    const userRoleName =
      authStore.currentUser?.role_details?.name || authStore.currentUser?.role || getJWTRole()

    // Vérification directe du rôle superadmin/admin AVANT toute autre logique
    if (
      userRoleName === 'superadmin' ||
      userRoleName === 'super_admin' ||
      userRoleName === 'admin' ||
      userRoleName === 'administrateur' ||
      userRoleName === 'SUPERADMIN' ||
      userRoleName === 'SUPER_ADMIN' ||
      userRoleName === 'ADMIN' ||
      userRoleName === 'ADMINISTRATEUR'
    ) {
      return true
    }

    // Éviter la récursion en vérifiant directement les permissions
    const result = permissions.every((permission) => userPermissions.value.includes(permission))
    return result
  }

  const getCurrentRole = () => {
    return authStore.currentUser?.role_details?.name || authStore.currentUser?.role || getJWTRole()
  }

  const isDentist = computed(() => {
    const roleName = getCurrentRole()
    return roleName === 'dentiste'
  })

  const isCoordinator = computed(() => {
    const roleName = getCurrentRole()
    return roleName === 'coordinateur/trice'
  })

  const isReferent = computed(() => {
    const roleName = getCurrentRole()
    return roleName === 'référent(e)'
  })

  const isAssistant = computed(() => {
    const roleName = getCurrentRole()
    return roleName === 'assistant(e)'
  })

  const isLaboratory = computed(() => {
    const roleName = getCurrentRole()
    return roleName === 'laboratoire'
  })

  const isAdmin = computed(() => {
    const roleName = getCurrentRole()
    return (
      roleName === 'admin' ||
      roleName === 'administrateur' ||
      roleName === 'ADMIN' ||
      roleName === 'ADMINISTRATEUR'
    )
  })

  const isSuperAdmin = computed(() => {
    const roleName = getCurrentRole()
    return (
      roleName === 'superadmin' ||
      roleName === 'super_admin' ||
      roleName === 'SUPERADMIN' ||
      roleName === 'SUPER_ADMIN'
    )
  })

  // Helper pour vérifier si l'utilisateur a des privilèges d'administration
  const hasAdminPrivileges = computed(() => {
    const roleName = getCurrentRole()
    const isAdminRole =
      roleName === 'admin' ||
      roleName === 'administrateur' ||
      roleName === 'superadmin' ||
      roleName === 'super_admin' ||
      roleName === 'ADMIN' ||
      roleName === 'ADMINISTRATEUR' ||
      roleName === 'SUPERADMIN' ||
      roleName === 'SUPER_ADMIN'

    return isAdminRole
  })

  const permissions = {
    // Dashboard et rapports
    canViewDashboard: computed(() => {
      // Tous les rôles peuvent voir le dashboard mais avec des contenus différents
      return true
    }),
    canViewReports: computed(() => hasPermission('view_reports')),

    // Administration système
    canAccessAdmin: computed(() => hasPermission('admin_access')),
    canAccessSuperAdmin: computed(() => hasPermission('superadmin_access')),

    // Gestion des utilisateurs
    canReadUsers: computed(() => hasPermission('read_users')),
    canWriteUsers: computed(() => hasPermission('write_users')),
    canDeleteUsers: computed(() => hasPermission('delete_users')),
    canReadOwnUser: computed(() => hasPermission('read_own_user')),

    // Gestion des rôles et permissions
    canReadRoles: computed(() => hasPermission('read_roles')),
    canWriteRoles: computed(() => hasPermission('write_roles')),
    canDeleteRoles: computed(() => hasPermission('delete_roles')),
    canManageRolePermissions: computed(() => hasPermission('manage_role_permissions')),

    canReadPermissions: computed(() => hasPermission('read_permissions')),
    canWritePermissions: computed(() => hasPermission('write_permissions')),
    canDeletePermissions: computed(() => hasPermission('delete_permissions')),

    // Gestion des produits/stock
    canReadProducts: computed(() => hasPermission('read_products')),
    canWriteProducts: computed(() => hasPermission('write_products')),
    canDeleteProducts: computed(() => hasPermission('delete_products')),

    // Gestion des commandes
    canReadOrders: computed(() => hasPermission('read_orders')),
    canWriteOrders: computed(() => hasPermission('write_orders')),
    canDeleteOrders: computed(() => hasPermission('delete_orders')),
    canReadOwnOrders: computed(() => hasPermission('read_own_orders')),
    canManageOrders: computed(() => hasPermission('manage_orders')),

    // Gestion des plannings
    canReadSchedules: computed(() => hasPermission('read_schedules')),
    canWriteSchedules: computed(() => hasPermission('write_schedules')),
    canDeleteSchedules: computed(() => hasPermission('delete_schedules')),
    canReadOwnSchedule: computed(() => hasPermission('read_own_schedule')),

    // Gestion des congés
    canReadLeaveRequests: computed(() => hasPermission('read_leave_requests')),
    canWriteLeaveRequests: computed(() => hasPermission('write_leave_requests')),
    canDeleteLeaveRequests: computed(() => hasPermission('delete_leave_requests')),
    canApproveLeaveRequests: computed(() => hasPermission('approve_leave_requests')),
    canReadOwnLeaveRequests: computed(() => hasPermission('read_own_leave_requests')),
    canManageLeaveRequests: computed(() => hasPermission('approve_leave_requests')),

    // Gestion des patients
    canReadPatients: computed(() => hasPermission('read_patients')),
    canWritePatients: computed(() => hasPermission('write_patients')),
    canDeletePatients: computed(() => hasPermission('delete_patients')),

    // Gestion des soins patients
    canReadPatientCares: computed(() => hasPermission('read_patient_cares')),
    canWritePatientCares: computed(() => hasPermission('write_patient_cares')),
    canDeletePatientCares: computed(() => hasPermission('delete_patient_cares')),
    canManagePatientCareStatus: computed(() => hasPermission('manage_patient_care_status')),
    canReadOwnPatientCares: computed(() => hasPermission('read_own_patient_cares')),

    // Gestion des traitements
    canReadTreatments: computed(() => hasPermission('read_treatments')),
    canWriteTreatments: computed(() => hasPermission('write_treatments')),
    canDeleteTreatments: computed(() => hasPermission('delete_treatments')),

    // Gestion des établissements
    canReadEstablishments: computed(() => hasPermission('read_establishments')),
    canWriteEstablishments: computed(() => hasPermission('write_establishments')),
    // Permissions composées pour l'accès aux sections
    canAccessCalendar: computed(() => {
      return hasPermission('read_schedules') || hasPermission('read_own_schedule')
    }),

    canAccessStock: computed(() => {
      // Seuls référent, admin et superadmin ont accès à la gestion du stock
      return hasPermission('write_products') || hasPermission('delete_products')
    }),

    canAccessOrders: computed(() => {
      return (
        hasPermission('read_orders') ||
        hasPermission('read_own_orders') ||
        hasPermission('write_orders')
      )
    }),

    canAccessPatientCare: computed(() => {
      return (
        hasPermission('read_patients') ||
        hasPermission('read_patient_cares') ||
        hasPermission('read_own_patient_cares')
      )
    }),

    // Permissions spécifiques pour coordinateur/trice
    canManagePatients: computed(() => {
      return hasPermission('read_patients') && hasPermission('write_patients')
    }),

    canFullyManagePatientCare: computed(() => {
      return (
        hasPermission('read_patient_cares') &&
        hasPermission('write_patient_cares') &&
        hasPermission('manage_patient_care_status')
      )
    }),

    canAccessLeaveRequests: computed(() => {
      return (
        hasPermission('read_leave_requests') ||
        hasPermission('read_own_leave_requests') ||
        hasPermission('approve_leave_requests')
      )
    }),

    canAccessUserManagement: computed(() => {
      return hasPermission('admin_access') && hasPermission('read_users')
    }),

    // Permissions spécifiques pour les plannings (compatibilité avec les composants existants)
    canCreateSchedule: computed(() => {
      return hasPermission('write_schedules') || hasAdminPrivileges.value
    }),
    canEditSchedule: computed(() => {
      return hasPermission('write_schedules') || hasAdminPrivileges.value
    }),
    canDeleteSchedule: computed(() => {
      return hasPermission('delete_schedules') || hasAdminPrivileges.value
    }),
    canManageSchedules: computed(() => {
      return (
        hasPermission('write_schedules') ||
        hasPermission('delete_schedules') ||
        hasAdminPrivileges.value
      )
    }),

    // Payslip permissions
    canReadPayslips: computed(() => {
      return hasPermission('read_payslips')
    }),

    canReadOwnPayslips: computed(() => {
      // Tous les utilisateurs connectés peuvent voir leurs propres fiches de paie
      return hasPermission('read_own_payslips') || true
    }),

    canWritePayslips: computed(() => {
      return hasPermission('write_payslips')
    }),

    canDeletePayslips: computed(() => {
      return hasPermission('delete_payslips')
    }),

    canUploadPayslips: computed(() => {
      return hasPermission('upload_payslips')
    }),

    canDownloadOwnPayslips: computed(() => {
      // Tous les utilisateurs connectés peuvent télécharger leurs propres fiches
      return hasPermission('download_own_payslips') || true
    }),

    // Gestion des binômes
    canReadDuos: computed(() => hasPermission('read_duos')),
    canReadOwnDuos: computed(() => hasPermission('read_own_duos')),
    canWriteDuos: computed(() => hasPermission('write_duos')),
    canDeleteDuos: computed(() => hasPermission('delete_duos')),

    // Permissions composées pour les binômes
    canAccessDuos: computed(() => {
      return hasPermission('read_duos') || hasPermission('read_own_duos')
    }),

    canManageDuos: computed(() => {
      return hasPermission('write_duos') || hasPermission('delete_duos')
    }),

    // Permissions laboratoire
    canViewLabPage: computed(() => hasPermission('view_lab_page')),
    canCreateLabForm: computed(() => hasPermission('create_lab_form')),
    canViewLabHistory: computed(() => hasPermission('view_lab_history')),

    // Permissions composées pour le laboratoire
    canAccessLab: computed(() => {
      return (
        hasPermission('view_lab_page') ||
        hasPermission('create_lab_form') ||
        hasPermission('view_lab_history')
      )
    }),

    // Permissions comptabilité
    canReadAccounting: computed(() => hasPermission('read_accounting')),
    canWriteAccounting: computed(() => hasPermission('write_accounting')),
    canDeleteAccounting: computed(() => hasPermission('delete_accounting')),
    canExportAccounting: computed(() => hasPermission('export_accounting')),
    canViewAccountingReports: computed(() => hasPermission('view_accounting_reports')),
    canManageAccountingCategories: computed(() => hasPermission('manage_accounting_categories')),

    // Permissions composées pour la comptabilité
    canAccessAccounting: computed(() => {
      return hasPermission('read_accounting') || hasPermission('view_accounting_reports')
    }),

    canManageAccounting: computed(() => {
      return (
        hasPermission('write_accounting') ||
        hasPermission('delete_accounting') ||
        hasPermission('manage_accounting_categories')
      )
    })
  }

  const isEstablishmentBypassed = computed(() => authStore.isEstablishmentBypassed)
  const currentEstablishment = computed(() => authStore.currentEstablishment)
  const hasMultipleEstablishments = computed(() => authStore.hasMultipleEstablishments)

  return {
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getCurrentRole,
    isDentist,
    isCoordinator,
    isReferent,
    isAssistant,
    isLaboratory,
    isAdmin,
    isSuperAdmin,
    hasAdminPrivileges,
    isEstablishmentBypassed,
    currentEstablishment,
    hasMultipleEstablishments,
    ...permissions
  }
}
